import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowBack,
  CheckCircle,
  Security,
  Refresh,
  Cancel,
  ErrorOutline,
  Warning, // Added warning icon
  Close as CloseIcon // Added for the AJK Dialog header
} from '@mui/icons-material';

import Newheader from "../../NewHeader/Newheader";
import Newfooter from "../../NewFooter/Newfooter";

import {
  VIPSDataServiceURL,
  requestOptions,
  RequiredField 
} from '../../../Constants/GlobalConstant';

// --- AJK POP UP THEME CONSTANTS ---
const HEADER_BG = '#1e293b'; 
const PRIMARY_COLOR_AJK = '#1565c0'; 
const BUTTON_GRADIENT = 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)';

const dialogHeaderStyle = {
  backgroundColor: HEADER_BG, 
  color: '#ffffff !important', 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center', 
  padding: '16px 24px', 
  borderBottom: `4px solid ${PRIMARY_COLOR_AJK}`
};

// --- CONFIGURATION ---
const CONFIG = {
  ENDPOINTS: {
    GET_QUESTION: '/User/GetUserSecurityQuestion',
    VERIFY_ANSWER: '/User/VerifyUserSecurityQuestionAnswer',
    UPDATE_PASSWORD: '/User/UpdatePassword'
  },
  COLORS: {
    PRIMARY: '#007bff',
    PRIMARY_HOVER: '#0056b3',
    ERROR: '#d32f2f',
    ERROR_BG: '#fff4f4',
    ERROR_BORDER: '#ffcdd2',
    SUCCESS: '#2e7d32',
    SUCCESS_BG: '#edf7ed',
    SUCCESS_BORDER: '#c8e6c9',
    WARNING: '#ed6c02',
    INFO: '#0288d1',
    TEXT_MAIN: '#222',
    TEXT_LABEL: '#d32f2f',
    BG_MAIN: '#f0f2f5',
    BG_DISABLED_INPUT: '#f5f5f5'
  }
};

// --- TEXT RESOURCES ---
const TEXTS = {
    title: "Recover Password",
    subtitle: "Follow the steps below to reset your password.",
    userIdLabel: "User ID",
    questionLabel: "Secret Question",
    answerLabel: "Secret Answer",
    newPassLabel: "New Password",
    confirmPassLabel: "Confirm Password",
    verifyBtn: "Verify Answer",
    verifying: "Verifying...",
    submitBtn: "Save Changes",
    submitting: "Updating Password...",
    backBtn: "Back to Login",
    resetBtn: "Reset Form",
    closeBtn: "Close",
    loginBtn: "Go to Login",
    strengthTitle: "Strength:",
    weak: "Weak", fair: "Fair", strong: "Strong", perfect: "Perfect",
    ruleUpper: "One uppercase letter",
    ruleNum: "One number",
    ruleSpecial: "One special character",
    alertAction: "Action Required",
    alertVerified: "Verified",
    successTitle: "Success",
    errorTitle: "Error",
    errNoUser: "Please enter User ID and Secret Answer first.",
    errVerifyFirst: "Please verify your security answer first.",
    errComplexity: "Password must meet all complexity requirements.", // Simplified text
    errNoId: "User ID does not exist in the VIPS application",
    errVerifyFail: "Verification failed.",
    errIncorrectAnswer: "Incorrect Security Answer.",
    errUpdateFail: "Update failed",
    match: "Passwords must match",
    required: RequiredField,
    opCompleted: "Operation completed."
};

// --- API SERVICE ---
const AuthService = {
  getSecurityQuestion: async (userId) => {
    try {
      const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
      const response = await axios.post(
        `${cleanUrl}${CONFIG.ENDPOINTS.GET_QUESTION}`,
        {},
        {
          params: { userId },
          headers: { ...requestOptions.headers, 'Content-Type': 'application/json' }
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  verifyAnswer: async (userId, secretAnswer) => {
    try {
      const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
      const response = await axios.post(
        `${cleanUrl}${CONFIG.ENDPOINTS.VERIFY_ANSWER}`,
        {},
        {
          params: { userId, secretAnswer },
          headers: { ...requestOptions.headers, 'Content-Type': 'application/json' }
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  updatePassword: async (userId, newPassword) => {
    const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
    const params = new URLSearchParams();
    params.append('userId', userId);
    params.append('newPassword', newPassword);
    try {
      const response = await axios.post(
        `${cleanUrl}${CONFIG.ENDPOINTS.UPDATE_PASSWORD}?${params.toString()}`,
        {},
        { headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// --- HELPER TO DECODE HTML ENTITIES ---
// Fixes the issue where regex comes as "&quot;" instead of "
const decodeHtmlEntities = (str) => {
    if (!str) return '';
    const txt = document.createElement("textarea");
    txt.innerHTML = str;
    return txt.value;
};

const Forgotpassword = () => {
  const navigate = useNavigate();
  const t = TEXTS;

  const [formData, setFormData] = useState({
    userId: '',
    secretQuestion: '',
    secretAnswer: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [verifyingAnswer, setVerifyingAnswer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [formStatus, setFormStatus] = useState({ type: '', msg: '' });
  const [dialogState, setDialogState] = useState({ open: false, title: '', message: '', isSuccess: false });

  // Dynamic Configuration State
  const [minPassLength, setMinPassLength] = useState(8);
  const [passComplexityRegex, setPassComplexityRegex] = useState(null);
  const [strength, setStrength] = useState({ level: '', width: '0%', color: '' });
  const [passwordRules, setPasswordRules] = useState({ length: false, uppercase: false, number: false, special: false });

  // --- FETCH CONFIGURATION ---
  useEffect(() => {
    const fetchParameter = async (paramName) => {
        try {
            const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
            const response = await axios.post(
                `${cleanUrl}/Misc/GetSystemParameterValueByName`, 
                {}, 
                { 
                    params: { name: paramName }, 
                    headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } 
                }
            );
            if (response.data) {
                return String(response.data)
                    .replace(/^"|"$/g, '')          
                    .replace(/(\r\n|\n|\r)/gm, "")  
                    .trim();
            }
            return null;
        } catch (err) {
            console.warn(`[Config] Error fetching ${paramName}:`, err);
            return null;
        }
    };

    const loadConfig = async () => {
        const len = await fetchParameter('MinimumPasswordLength');
        if (len) setMinPassLength(Number(len));
        
        const passRegex = await fetchParameter('PasswordComplexity');
        if (passRegex) {
            // FIX: Decode the regex before storing it
            setPassComplexityRegex(decodeHtmlEntities(passRegex));
        }
    };
    loadConfig();
  }, []);

  const setStatus = (type, msg) => setFormStatus({ type, msg });

  // --- HANDLERS ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'newPassword') checkStrength(value);
  };

  const handleUserIdBlur = async () => {
    const userIdRaw = formData.userId;
    if (!userIdRaw) return;

    // 1. Trim spaces
    const userId = userIdRaw.trim();
    if (userId !== userIdRaw) {
        setFormData(prev => ({ ...prev, userId }));
    }

    setIsVerified(false);
    setFormData(prev => ({ ...prev, newPassword: '', confirmPassword: '', secretAnswer: '', secretQuestion: '' }));
    setStrength({ level: '', width: '0%', color: '' });

    setLoadingQuestion(true);
    setStatus('', '');

    try {
      const data = await AuthService.getSecurityQuestion(userId);
      
      let question = "";
      if (typeof data === 'string') {
          question = data;
      } else if (data) {
          question = data.question || 
                     data.Question || 
                     data.secretQuestion || 
                     data.SecretQuestion || 
                     (typeof data.d === 'string' ? data.d : (data.d?.Question || data.d?.question));
      }

      question = question ? question.trim() : "";

      if (question.length > 0) {
        setFormData(prev => ({ ...prev, secretQuestion: question }));
      } else {
        setStatus('error', t.errNoId);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      const errMsg = err.response?.data?.Message || t.errNoId;
      setStatus('error', errMsg);
    } finally {
      setLoadingQuestion(false);
    }
  };

  const handleVerifyAnswer = async () => {
    const { userId, secretAnswer } = formData;
    if (!userId || !secretAnswer) {
      setStatus('error', t.errNoUser);
      setErrors(prev => ({
        ...prev,
        userId: !userId ? t.required : '',
        secretAnswer: !secretAnswer ? t.required : ''
      }));
      return;
    }
    setVerifyingAnswer(true);
    setStatus('', '');
    try {
      const response = await AuthService.verifyAnswer(userId, secretAnswer);
      const isValid = response === true || response === "True" || response?.d === true || response?.Success === true;

      if (isValid) {
        setIsVerified(true);
        setStatus('success', t.alertVerified);
      } else {
        setIsVerified(false);
        setStatus('error', t.errIncorrectAnswer);
      }
    } catch (error) {
      setIsVerified(false);
      setStatus('error', t.errVerifyFail);
    } finally {
      setVerifyingAnswer(false);
    }
  };

  // --- UPDATED PASSWORD LOGIC ---
  const checkStrength = (password) => {
    // 1. Basic Rules
    const lengthValid = password.length >= minPassLength;
    const uppercaseValid = /[A-Z]/.test(password);
    const numberValid = /[0-9]/.test(password);
    const specialValid = /[^A-Za-z0-9]/.test(password);

    // 2. Server Regex Check (Optional)
    let matchesServerRegex = true;
    if (passComplexityRegex) {
        try {
            // Because we decoded in loadConfig, this now works correctly!
            const regex = new RegExp(passComplexityRegex);
            matchesServerRegex = regex.test(password);
        } catch (e) { matchesServerRegex = false; }
    }

    // 3. Update Visual Checklist
    const rules = { length: lengthValid, uppercase: uppercaseValid, number: numberValid, special: specialValid };
    setPasswordRules(rules);

    // 4. Determine Level
    if (!password) {
        setStrength({ level: '', width: '0%', color: '' });
        return;
    }

    if (!lengthValid) {
        setStrength({ level: t.weak, width: '25%', color: CONFIG.COLORS.ERROR });
        return;
    }

    const basicCount = [uppercaseValid, numberValid].filter(Boolean).length;

    if (basicCount === 2 && specialValid && matchesServerRegex) {
        setStrength({ level: t.perfect, width: '100%', color: CONFIG.COLORS.SUCCESS });
    } else if (basicCount === 2 && matchesServerRegex) {
        setStrength({ level: t.strong, width: '75%', color: CONFIG.COLORS.INFO });
    } else if (basicCount >= 1) {
        setStrength({ level: t.fair, width: '50%', color: CONFIG.COLORS.WARNING });
    } else {
        setStrength({ level: t.weak, width: '25%', color: CONFIG.COLORS.ERROR });
    }
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();
    if (!isVerified) { setStatus('error', t.errVerifyFirst); return; }
    
    // Validate Password
    const newErrors = {};
    if (!formData.newPassword) {
        newErrors.newPassword = t.required;
    } else {
        // 1. Length Check
        if (formData.newPassword.length < minPassLength) {
            newErrors.newPassword = `Minimum ${minPassLength} characters required`;
        } 
        // 2. Regex Check (Explicit check is better than checking UI color)
        else if (passComplexityRegex) {
            try {
                const regex = new RegExp(passComplexityRegex);
                if (!regex.test(formData.newPassword)) {
                    newErrors.newPassword = "Password must contain uppercase, number, and special character.";
                }
            } catch(e) { console.warn("Regex check failed", e); }
        }
    }
    
    if (!formData.confirmPassword) newErrors.confirmPassword = t.required;
    else if (formData.confirmPassword !== formData.newPassword) newErrors.confirmPassword = t.match;

    if (Object.keys(newErrors).length > 0) { 
        setErrors(newErrors); 
        // Show validation error in popup as well (optional, matches your style)
        const errorMsg = Object.values(newErrors)[0];
        setDialogState({ 
            open: true, 
            title: "Validation Error", 
            message: errorMsg, 
            isSuccess: false 
        });
        return; 
    }

    setSubmitting(true);
    setStatus('', '');

    try {
      const response = await AuthService.updatePassword(formData.userId, formData.newPassword);
      const success = response?.success || response?.Success || response === true;
      const message = response?.message || response?.Message || t.opCompleted;

      setDialogState({
        open: true,
        title: success ? t.successTitle : t.errorTitle,
        message: message,
        isSuccess: success
      });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t.errUpdateFail;
      setDialogState({ open: true, title: t.errorTitle, message: msg, isSuccess: false });
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to determine icon color/type for the dialog (Updated for AJK Dark Header)
  const getDialogIcon = () => {
    if (dialogState.isSuccess) return <CheckCircle sx={{ color: '#ffffff !important' }} />;
    if (dialogState.title === "Validation Error") return <Warning sx={{ color: '#ffffff !important' }} />;
    return <ErrorOutline sx={{ color: '#ffffff !important' }} />;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: CONFIG.COLORS.BG_MAIN }}>
      <Newheader />
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', pt: 4, pb: 8 }}>
        <Container maxWidth="md">
          <Paper elevation={6} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, position: 'relative' }}>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: CONFIG.COLORS.TEXT_MAIN, mb: 1, letterSpacing: '-0.5px' }}>{t.title}</Typography>
              <Box sx={{ width: '60px', height: '4px', backgroundColor: CONFIG.COLORS.ERROR, mx: 'auto', mb: 2, borderRadius: 2 }} />
              <Typography variant="body1" color="textSecondary">{t.subtitle}</Typography>
            </Box>

            <Collapse in={!!formStatus.msg}>
              <Alert 
                severity={formStatus.type === 'success' ? 'success' : 'error'}
                icon={formStatus.type === 'success' ? <CheckCircle fontSize="inherit" /> : <ErrorOutline fontSize="inherit" />}
                sx={{ mb: 4, borderRadius: 2 }}
                onClose={() => setStatus('', '')}
              >
                {formStatus.msg}
              </Alert>
            </Collapse>

            <form onSubmit={onFormSubmit}>
              <Grid container spacing={3} alignItems="center">
                <FormRow label={t.userIdLabel} name="userId" value={formData.userId} onChange={handleChange} error={errors.userId} onBlur={handleUserIdBlur} readOnly={isVerified} endAdornment={loadingQuestion && <CircularProgress size={20} />} />
                <Grid item xs={12} md={4}><Typography variant="subtitle1" fontWeight={600} sx={{ color: CONFIG.COLORS.TEXT_LABEL }}>{t.questionLabel}*:</Typography></Grid>
                <Grid item xs={12} md={8}>
                  <TextField fullWidth size="small" disabled variant="outlined" value={formData.secretQuestion} sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#333', fontWeight: 500, backgroundColor: CONFIG.COLORS.BG_DISABLED_INPUT } }} />
                </Grid>
                <FormRow label={t.answerLabel} name="secretAnswer" value={formData.secretAnswer} onChange={handleChange} error={errors.secretAnswer} readOnly={isVerified} endAdornment={isVerified && <CheckCircle color="success" />} />
                {!isVerified && (
                  <>
                    <Grid item xs={12} md={4} />
                    <Grid item xs={12} md={8}>
                      <Button variant="contained" onClick={handleVerifyAnswer} disabled={verifyingAnswer} startIcon={verifyingAnswer ? <CircularProgress size={20} color="inherit" /> : <Security />} sx={{ bgcolor: CONFIG.COLORS.PRIMARY, color: '#fff', fontWeight: 'bold', borderRadius: 2, py: 1, '&:hover': { bgcolor: CONFIG.COLORS.PRIMARY_HOVER }, width: { xs: '100%', sm: 'auto' } }}>
                        {verifyingAnswer ? t.verifying : t.verifyBtn}
                      </Button>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Collapse in={isVerified} unmountOnExit>
                    <Grid container spacing={3} alignItems="flex-start">
                      <PasswordRow label={t.newPassLabel} name="newPassword" value={formData.newPassword} onChange={handleChange} error={errors.newPassword} show={showPassword} toggle={() => setShowPassword(!showPassword)} showStrengthMeter={true} strength={strength} passwordRules={passwordRules} minPassLength={minPassLength} t={t} />
                      <PasswordRow label={t.confirmPassLabel} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} error={errors.confirmPassword} show={showConfirm} toggle={() => setShowConfirm(!showConfirm)} t={t} />
                      <Grid item xs={12} md={4} />
                      <Grid item xs={12} md={8}>
                        <Button type="submit" variant="contained" disabled={submitting} fullWidth sx={{ py: 1.5, fontWeight: 'bold', fontSize: "16px", borderRadius: 2, mt: 2, backgroundColor: CONFIG.COLORS.PRIMARY, '&:hover': { backgroundColor: CONFIG.COLORS.PRIMARY_HOVER } }}>
                          {submitting ? t.submitting : t.submitBtn}
                        </Button>
                      </Grid>
                    </Grid>
                  </Collapse>
                </Grid>
                <Grid item xs={12} md={4} />
                <Grid item xs={12} md={8}>
                  <Button variant="outlined" fullWidth onClick={() => isVerified ? window.location.reload() : navigate('/')} startIcon={isVerified ? <Refresh /> : <ArrowBack />} sx={{ py: 1.2, borderRadius: 2, borderColor: '#ccc', color: '#555', fontWeight: 600, '&:hover': { borderColor: CONFIG.COLORS.PRIMARY, color: CONFIG.COLORS.PRIMARY, backgroundColor: '#f0f8ff' } }}>
                    {isVerified ? t.resetBtn : t.backBtn}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Container>
      </Box>

      {/* RESULT DIALOG - UPDATED TO AJK THEME */}
      <Dialog 
        open={dialogState.open} 
        onClose={() => setDialogState(prev => ({ ...prev, open: false }))} 
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
      >
        <DialogTitle sx={dialogHeaderStyle}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {getDialogIcon()}
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff !important', letterSpacing: '0.5px' }}>
                    {dialogState.title}
                </Typography>
            </Box>
            <IconButton 
                onClick={() => { 
                    setDialogState(prev => ({ ...prev, open: false })); 
                    if (dialogState.isSuccess) navigate('/'); 
                }} 
                sx={{ color: '#ffffff !important' }}
            >
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ backgroundColor: '#fafafa', py: 4 }} dividers>
            <DialogContentText sx={{ color: '#334155', fontWeight: 500, fontSize: '1rem', whiteSpace: 'pre-line' }}>
                {dialogState.message}
            </DialogContentText>
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: '#f4f2e9', p: 3, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
            <Button 
                onClick={() => { 
                    setDialogState(prev => ({ ...prev, open: false })); 
                    if (dialogState.isSuccess) navigate('/'); 
                }} 
                variant="contained" 
                autoFocus 
                sx={{ background: BUTTON_GRADIENT, px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
                {dialogState.isSuccess ? t.loginBtn : t.closeBtn}
            </Button>
        </DialogActions>
      </Dialog>
      <Newfooter />
    </Box>
  );
};

// --- HELPERS ---
const FormRow = ({ label, name, value, onChange, error, type = "text", endAdornment, onBlur, readOnly }) => (
  <>
    <Grid item xs={12} md={4}><Typography variant="subtitle1" fontWeight={600} sx={{ color: CONFIG.COLORS.TEXT_LABEL }}>{label}*:</Typography></Grid>
    <Grid item xs={12} md={8}>
      <TextField name={name} value={value} onChange={onChange} fullWidth type={type} size="small" variant="outlined" error={!!error} helperText={error} InputProps={{ endAdornment, readOnly: !!readOnly, sx: { borderRadius: 1.5 } }} disabled={!!readOnly} onBlur={onBlur} />
    </Grid>
  </>
);

const PasswordRow = ({ label, name, value, onChange, error, show, toggle, showStrengthMeter, strength, passwordRules, t, minPassLength }) => (
  <>
    <Grid item xs={12} md={4} sx={{ pt: { md: 1 } }}><Typography variant="subtitle1" fontWeight={600} sx={{ color: CONFIG.COLORS.TEXT_LABEL }}>{label}*:</Typography></Grid>
    <Grid item xs={12} md={8}>
      <TextField name={name} value={value} onChange={onChange} fullWidth type={show ? 'text' : 'password'} size="small" variant="outlined" error={!!error} helperText={error} InputProps={{ sx: { borderRadius: 1.5 }, endAdornment: (<InputAdornment position="end"><IconButton onClick={toggle} edge="end" size="small">{show ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) }} />
      {showStrengthMeter && strength && (
        <Box sx={{ mt: 1, p: 2, bgcolor: '#fff', border: '1px solid #eee', borderRadius: 2 }}>
          <Box sx={{ height: 6, width: '100%', bgcolor: '#f0f0f0', borderRadius: 5, overflow: 'hidden' }}><Box sx={{ height: '100%', width: strength.width, bgcolor: strength.color, transition: 'width 0.3s' }} /></Box>
          <Box display="flex" justifyContent="space-between" mt={1}><Typography variant="caption" color="textSecondary">{t.strengthTitle}</Typography><Typography variant="caption" sx={{ color: strength.color, fontWeight: 'bold' }}>{strength.level || ''}</Typography></Box>
          <List dense sx={{ p: 0, mt: 1 }}>
            <StrengthItem label={`Minimum ${minPassLength} characters`} valid={passwordRules.length} />
            <StrengthItem label={t.ruleUpper} valid={passwordRules.uppercase} />
            <StrengthItem label={t.ruleNum} valid={passwordRules.number} />
            <StrengthItem label={t.ruleSpecial} valid={passwordRules.special} />
          </List>
        </Box>
      )}
    </Grid>
  </>
);

const StrengthItem = ({ label, valid }) => (
  <ListItem sx={{ py: 0, px: 0 }}>
    <ListItemIcon sx={{ minWidth: 24 }}>{valid ? <CheckCircle fontSize="inherit" color="success" sx={{ fontSize: 16 }} /> : <Cancel fontSize="inherit" color="disabled" sx={{ fontSize: 16 }} />}</ListItemIcon>
    <ListItemText primary={label} primaryTypographyProps={{ variant: 'caption', color: valid ? 'text.primary' : 'text.disabled' }} />
  </ListItem>
);

export default Forgotpassword;